import type { ReportTheme } from "@/components/ui/report/reportTheme";
import { parseGoogleFontsStylesheetUrl } from "@/lib/template-studio/brandAssets";
import type {
  ChartTheme,
  TemplateLayoutTheme,
} from "@/types/template-layout/templateLayout.types";

// The chart image bundle imports this module; keep it free of app API and env-backed imports.

export interface ResolvedTemplateStudioReportTheme {
  reportTheme: Partial<ReportTheme>;
  fontStylesheetUrl: string | null;
}

const resolveChartPalette = (
  chart: ChartTheme,
): [string, string, string, string, string] => {
  const palette = chart.categoricalPalette;
  if (chart.strategy !== "monochromatic" && palette?.length) {
    const colorAt = (index: number) =>
      palette[index % palette.length] ?? chart.baseColor;
    return [colorAt(0), colorAt(1), colorAt(2), colorAt(3), colorAt(4)];
  }

  const baseColor = chart.baseColor;
  return [
    baseColor,
    `color-mix(in srgb, ${baseColor} 75%, white)`,
    `color-mix(in srgb, ${baseColor} 75%, black)`,
    `color-mix(in srgb, ${baseColor} 50%, white)`,
    `color-mix(in srgb, ${baseColor} 55%, black)`,
  ];
};

/**
 * Maps persisted layout theme to {@link ReportThemeProvider} input and font load URL.
 * Returns empty theme when layout theme is absent.
 */
export const resolveReportThemeFromLayoutTheme = (
  theme: TemplateLayoutTheme | null | undefined,
): ResolvedTemplateStudioReportTheme => {
  if (theme == null) {
    return { reportTheme: {}, fontStylesheetUrl: null };
  }

  const googleFont =
    theme.fontStylesheetUrl != null
      ? parseGoogleFontsStylesheetUrl(theme.fontStylesheetUrl)
      : null;

  const reportTheme: Partial<ReportTheme> = {
    primaryColor: theme.primaryColor,
    chartPalette: resolveChartPalette(theme.chart),
    reportSection: theme.reportSection,
    reportHighlight: theme.reportHighlight,
  };

  const fontFamily = theme.fontFamily?.trim() || googleFont?.family || null;
  if (fontFamily != null) {
    reportTheme.fontFamily = `"${fontFamily}", sans-serif`;
  } else if (theme.reportSection != null || theme.reportHighlight != null) {
    reportTheme.fontFamily = "inherit";
  }

  return {
    reportTheme,
    fontStylesheetUrl: googleFont?.stylesheetUrl ?? null,
  };
};
